import { useCallback, useEffect, useMemo } from 'react';

import { Redirect, Route, BrowserRouter as Router } from 'react-router-dom';

import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { SecureRoute, Security, useOktaAuth } from '@okta/okta-react';
import { Switch } from 'react-router-dom/cjs/react-router-dom.min';

import { OktaRefresherComponent } from './OktaRefresherComponent';

import { PROVIDER_TRANSACTIONS_ROUTE, ROUTER_BASE_NAME, ROUTES_BASE } from '../../config/routes';
import { MapProvider } from '../../context/mapContext';
import { isV2PermissionsEnabled } from '../../helpers/featureFlags/isV2PermissionsEnabled';
import { isV2ProviderLocationsEnabled } from '../../helpers/featureFlags/isV2ProviderLocationsEnabled';
import { getLocationRoute } from '../../helpers/routes/getLocationRoute';
import { saveCurrentTabRouteToLocalStorage } from '../../helpers/routes/saveCurrentTabRouteToLocalStorage';
import history from '../../history';
import { ProviderLocationsV2 } from '../../pages/ProviderLocationsV2/ProviderLocationsV2';
import { ProviderLocationsContextProvider } from '../../store/ProviderLocationsContext';
import { ROLE, useHasAnyRole } from '../../utils/permissions';
import AddLocations from '../AddLocations/index';
import Analytics from '../Analytics/AnalyticsPage';
import Locations from '../Locations';
import MySurveysPage from '../MySurveys/MySurveysPage';
import OktaLogin from '../OktaLogin';
import { IsLogin } from '../OktaLogin/isLogin';
import CustomLoginCallback from '../OktaLogin/LoginCallback';
import ProviderDashboard from '../ProviderDashboard/ProviderDashboard';
import ProviderFileUpload from '../ProviderFileUpload/ProviderFileUpload';
import ProviderLocations from '../ProviderLocations/index';
import providerProfile from '../ProviderProfile';
import ProviderTransactions from '../ProviderTransactions/ProviderTransactions';
import RequestProvider from '../RequestAccess/RequestProvider';

const OKTA_LOGIN_CALLBACK_URL = '/';
const OKTA_AUTHORIZATION_URL = '/okta';
const MK2_REDIRECT_URL = window.location.origin + '/MKII';

export const OKTA_CLIENT_ID = process.env.REACT_APP_OKTA_CLIENT_ID.trim();

const RootRoute = () => {
  const { oktaAuth, authState } = useOktaAuth();

  if (oktaAuth.token.isLoginRedirect()) {
    return <CustomLoginCallback />;
  }

  if (authState?.isAuthenticated && !sessionStorage.getItem('ssoEmail')) {
    return <Redirect to={OKTA_AUTHORIZATION_URL} />;
  }

  return <Redirect to={getLocationRoute()} />;
};

export const RoutesV2 = () => {
  const oktaAuth = useMemo(
    () =>
      new OktaAuth({
        clientId: OKTA_CLIENT_ID,
        issuer: process.env.REACT_APP_OKTA_ISSUER_URL.trim(),
        redirectUri: MK2_REDIRECT_URL,
        scopes: ['offline_access', 'email', 'openid', 'profile'],
        logoutUrl: process.env.REACT_APP_OKTA_LOGOUT_URL.trim(),
        postLogoutRedirectUri: MK2_REDIRECT_URL + '/logout',
        tokenManager: {
          storageKey: `okta-token-storage-mkii-${OKTA_CLIENT_ID}`,
          autoRenew: false,
          syncStorage: false,
          expireEarlySeconds: 30,
        },
      }),
    [],
  );

  let userHasAccess = false;

  const { hasRoles } = useHasAnyRole([ROLE.BASIC_PROVIDER]);

  if (isV2PermissionsEnabled()) {
    userHasAccess = hasRoles;
  }

  const restoreOriginalUri = useCallback(async (_oktaAuth, originalUri) => {
    const fallbackRoute = !userHasAccess
      ? `${ROUTES_BASE}${getLocationRoute()}`
      : `${ROUTES_BASE}${PROVIDER_TRANSACTIONS_ROUTE}`;

    if (!originalUri) {
      history.replace(fallbackRoute);

      return;
    }

    const relativeUri = toRelativeUrl(originalUri, window.location.origin);

    if (relativeUri === ROUTES_BASE || relativeUri === `${ROUTES_BASE}/` || relativeUri === '/') {
      history.replace(fallbackRoute);

      return;
    }

    history.replace(relativeUri);
  }, []);

  const getTabId = useCallback(() => {
    let tabId = sessionStorage.getItem('tabId');

    if (!tabId) {
      const existingTabId = localStorage.getItem('existingTabId');
      const timeLastUpdated = Date.now() - (localStorage.getItem('lastUpdated') || Date.now());

      if (existingTabId && timeLastUpdated < 5 * 60 * 1000) {
        tabId = existingTabId;
      } else {
        tabId = Date.now().toString();
      }
      sessionStorage.setItem('tabId', tabId);
    }

    return tabId;
  }, []);

  useEffect(() => {
    const tabId = getTabId();
    const activeTabs = JSON.parse(localStorage.getItem('activeTabsMKII') || '[]');

    if (!activeTabs.includes(tabId)) {
      activeTabs.push(tabId);

      localStorage.setItem('activeTabsMKII', JSON.stringify(activeTabs));
    }

    saveCurrentTabRouteToLocalStorage();
  }, [getTabId]);

  useEffect(() => {
    const onBeforeUnload = () => {
      let tabId = sessionStorage.getItem('tabId');

      if (!tabId) {
        const existingTabId = localStorage.getItem('existingTabId');
        const timeLastUpdated = Date.now() - (localStorage.getItem('lastUpdated') || Date.now());

        if (existingTabId && timeLastUpdated < 5 * 60 * 1000) {
          tabId = existingTabId;
        } else {
          tabId = Date.now().toString();
        }
        sessionStorage.setItem('tabId', tabId);
      }

      const loginAccess = localStorage.getItem('MKII_access');

      if (loginAccess?.toLowerCase() !== 'yes') return;

      localStorage.setItem('lastUpdated', Date.now().toString());

      const activeTabs = JSON.parse(localStorage.getItem('activeTabsMKII') || '[]');

      const updatedActiveTabs = activeTabs.filter((id) => id !== tabId);

      localStorage.setItem('activeTabsMKII', JSON.stringify(updatedActiveTabs));
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  return (
    <Router basename={ROUTER_BASE_NAME}>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <OktaRefresherComponent />
        <ProviderLocationsContextProvider>
          <MapProvider>
            <IsLogin />
            <Route exact path={OKTA_LOGIN_CALLBACK_URL} component={RootRoute} />
            <SecureRoute exact path={OKTA_AUTHORIZATION_URL} component={OktaLogin} />

            <Route path="/requestAccess" component={RequestProvider} />
            <Switch>
              <Redirect
                exact
                from="/locations/place/:lng/:lat/:zoom"
                to="/locations/place/:lng/:lat/:zoom/default"
              />
              <SecureRoute
                exact
                path="/locations/place/:lng/:lat/:zoom/:basemap"
                component={Locations}
              />
            </Switch>

            <SecureRoute exact path="/analytics" component={Analytics} />
            <SecureRoute exact path="/myTransactions" component={MySurveysPage} />
            <SecureRoute path="/uploadFile" component={ProviderFileUpload} />
            <SecureRoute path="/providerDashboard" component={ProviderDashboard} />
            <SecureRoute path="/providerTransactions" component={ProviderTransactions} />
            <SecureRoute
              path="/providerLocations"
              component={isV2ProviderLocationsEnabled() ? ProviderLocationsV2 : ProviderLocations}
            />
            <SecureRoute path="/providerProfile" component={providerProfile} />
            <SecureRoute path="/addLocation/single" component={AddLocations} />
            <SecureRoute exact path="/logout" render={() => <Redirect to={getLocationRoute()} />} />
          </MapProvider>
        </ProviderLocationsContextProvider>
      </Security>
    </Router>
  );
};
